import CanvasPage from './components/Canvas/CanvasPage'
import PageTabs from './components/Canvas/PageTabs'
import ImportDecision from './components/Auth/ImportDecision'
import SyncCoordinator from './components/Sync/SyncCoordinator'
import SyncIndicator from './components/Sync/SyncIndicator'
import CommandPalette from './components/Common/CommandPalette'
import NodeNotchSidebar, { AppSection } from './components/Layout/NodeNotchSidebar'
import { useTaskStore } from './store/taskStore'
import { getCompletionStats } from './utils/taskUtils'
import { CheckCircle2, Clock, Circle, Search } from 'lucide-react'
import { lazy, Suspense, useEffect, useState } from 'react'

const TaskList = lazy(() => import('./components/Tasks/TaskList'))
const ProgressPage = lazy(() => import('./components/Progress/ProgressPage'))
const TaskGraphPage = lazy(() => import('./components/Graph/TaskGraphPage'))

function TopBar() {
  const tasks = useTaskStore((state) => state.tasks)
  const isDark = useTaskStore((state) => Boolean(state.accessibility.darkMode))
  const { done, inProgress, todo } = getCompletionStats(tasks)
  useEffect(() => {
    document.documentElement.classList.toggle('dark', isDark)
  }, [isDark])
  const openPalette = () => {
    window.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', ctrlKey: true, bubbles: true }))
  }
  return (
    <header className="flex shrink-0 items-center justify-between gap-4 border-b border-slate-700 bg-slate-900 px-4 py-2.5 sm:px-6">
      <div className="flex shrink-0 items-center">
        <span className="text-base font-bold tracking-tight text-white">TaskPriority</span>
      </div>

      <button
        type="button"
        onClick={openPalette}
        className="flex flex-1 max-w-xl items-center justify-between gap-3 rounded-xl bg-slate-800/90 hover:bg-slate-800 px-4 py-2 text-sm text-slate-400 border border-slate-700/60 shadow-inner transition-colors mx-auto"
        title="Buscar tarefas, comandos e notas (Ctrl+K)"
      >
        <div className="flex items-center gap-2.5 min-w-0">
          <Search size={16} className="text-slate-400 shrink-0" />
          <span className="truncate font-normal">Buscar tarefas ou digitar comandos...</span>
        </div>
        <kbd className="hidden sm:inline-block shrink-0 rounded bg-slate-700/80 px-2 py-0.5 text-xs font-semibold text-slate-300 border border-slate-600/50">Ctrl K</kbd>
      </button>

      <div className="flex shrink-0 items-center gap-4 text-sm">
        <div className="hidden items-center gap-4 lg:flex border-r border-slate-800 pr-4">
          <Stat icon={<CheckCircle2 size={14} className="text-green-400" />} value={done} label="Concluídas" />
          <Stat icon={<Clock size={14} className="text-blue-400" />} value={inProgress} label="Em andamento" />
          <Stat icon={<Circle size={14} className="text-slate-400" />} value={todo} label="A fazer" />
        </div>
        <SyncIndicator />
      </div>
    </header>
  )
}

function Stat({ icon, value, label }: { icon: React.ReactNode; value: number; label: string }) {
  return <div className="flex items-center gap-1.5 text-slate-300">{icon}<span className="font-semibold text-white">{value}</span><span className="hidden text-xs text-slate-400 sm:inline">{label}</span></div>
}

export default function App() {
  const simpleNavigation = useTaskStore((state) => state.accessibility.simpleNavigation)
  const [section, setSection] = useState<AppSection>('workspace')
  return (
    <div className="flex h-screen w-screen overflow-hidden bg-slate-100 text-slate-900 dark:bg-slate-950 dark:text-slate-100">
      <SyncCoordinator />
      <ImportDecision />
      <CommandPalette />
      <NodeNotchSidebar currentSection={section} onSelectSection={setSection} />
      <div className="flex flex-1 flex-col overflow-hidden min-w-0">
        <TopBar />
        {section === 'workspace' && !simpleNavigation && <PageTabs />}
        <div className="flex-1 overflow-hidden">
          <div className="h-full overflow-auto">
            <Suspense fallback={<p className="p-6 text-slate-500 dark:text-slate-400">Carregando…</p>}>
              {section === 'progress' ? (
                <ProgressPage />
              ) : section === 'graph' ? (
                <TaskGraphPage />
              ) : simpleNavigation ? (
                <main className="mx-auto min-h-full max-w-4xl bg-white dark:bg-slate-900"><TaskList /></main>
              ) : (
                <CanvasPage />
              )}
            </Suspense>
          </div>
        </div>
      </div>
    </div>
  )
}
